import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useUserSettingsUiMutation } from '../../app/query-hooks/use-user-settings-ui';
import { useActivateManagedTemplate } from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-activate-managed-template';
import { useCustomTemplate } from '../../company-settings/tabs/job-architecture-tab/job-profile/api/use-custom-template';
import { DocumentsUploadSection } from '../../company-settings/tabs/organization-tab/knowledge-base/DocumentsUploadSection';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { ConfirmProvider } from '../providers/ConfirmProvider';
import { ButtonGroup, Container, Description, Footer, FooterActions, Title, TitleGroup } from './setup-dialog-styles';

type UploadDataContentProps = {
    readonly jobArchitectureId: string;
    readonly onBack?: () => void;
    readonly onComplete: () => void;
};

export const UploadDataContent: FunctionComponent<UploadDataContentProps> = ({
    jobArchitectureId,
    onBack,
    onComplete,
}) => {
    const translate = useTranslate();
    const { applicationState, validatingCobrainerApiFetcher } = useApplicationContext();
    const { userSettingsUiMutation } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);
    const { generateJobArchitectureWithAI } = useApplicationFetchers();
    const { showErrorSnackbar } = useSnackbars();

    const isCreateAllEnabled = applicationState.featureFlagService.isFeatureFlagEnabled(
        'generate-all-families-clusters-roles-button',
    );

    const customTemplateQuery = useCustomTemplate(jobArchitectureId, isCreateAllEnabled);
    const { activate } = useActivateManagedTemplate(jobArchitectureId, isCreateAllEnabled);

    const [isBuildLoading, setIsBuildLoading] = useState(false);
    const [isSkipLoading, setIsSkipLoading] = useState(false);
    const [isCreateAllLoading, setIsCreateAllLoading] = useState(false);

    const isLoading = isBuildLoading || isSkipLoading || isCreateAllLoading;

    const handleGenerate = async (
        awaitKnowledgeBaseIngestion: boolean,
        setLoading: (loading: boolean) => void,
    ): Promise<void> => {
        setLoading(true);
        const result = await generateJobArchitectureWithAI({
            awaitKnowledgeBaseIngestion,
            jobArchitectureId,
            type: 'FAMILIES_AND_CLUSTERS',
        });

        if (result.isOk) {
            userSettingsUiMutation.mutate({ selectedJobArchitectureId: jobArchitectureId });
            onComplete();
        } else {
            showErrorSnackbar(result.error);
        }
        setLoading(false);
    };

    const handleBuildJobArchitecture = (): Promise<void> => handleGenerate(true, setIsBuildLoading);
    const handleSkip = (): Promise<void> => handleGenerate(false, setIsSkipLoading);

    const handleCreateAll = async (): Promise<void> => {
        setIsCreateAllLoading(true);
        try {
            if (is.nullOrUndefined(customTemplateQuery.data)) {
                const activated = await activate();
                if (!activated) {
                    showErrorSnackbar(new Error('Failed to activate default job profile template'));
                    return;
                }
            }

            const result = await generateJobArchitectureWithAI({
                awaitKnowledgeBaseIngestion: true,
                jobArchitectureId,
                type: 'families-clusters-and-roles',
            });

            if (result.isOk) {
                userSettingsUiMutation.mutate({ selectedJobArchitectureId: jobArchitectureId });
                onComplete();
            } else {
                showErrorSnackbar(result.error);
            }
        } catch (error) {
            showErrorSnackbar(error);
        } finally {
            setIsCreateAllLoading(false);
        }
    };

    return (
        <Container>
            <div className="mb-8xl">
                <TitleGroup>
                    <Title>{translate('pages.job-architecture-v2.setup-dialog.upload-data.title')}</Title>
                    <Description>
                        {translate('pages.job-architecture-v2.setup-dialog.upload-data.description')}
                    </Description>
                </TitleGroup>
                <ConfirmProvider>
                    <DocumentsUploadSection />
                </ConfirmProvider>
            </div>
            <Footer>
                <FooterActions style={is.undefined(onBack) ? { justifyContent: 'flex-end' } : undefined}>
                    {!is.undefined(onBack) && (
                        <Button variant="tertiary" onClick={onBack}>
                            {translate('common.back')}
                        </Button>
                    )}
                    <ButtonGroup>
                        <Button disabled={isLoading} variant="tertiary" onClick={handleSkip}>
                            {isSkipLoading && <Spinner />}
                            {translate('common.skip')}
                        </Button>
                        {isCreateAllEnabled && (
                            <Button disabled={isLoading} variant="secondary" onClick={handleCreateAll}>
                                {isCreateAllLoading && <Spinner />}
                                {translate(
                                    'pages.job-architecture-v2.setup-dialog.generate-all-families-clusters-roles',
                                )}
                            </Button>
                        )}
                        <Button disabled={isLoading} onClick={handleBuildJobArchitecture}>
                            {isBuildLoading && <Spinner />}
                            {translate('pages.job-architecture-v2.setup-dialog.build-job-architecture')}
                        </Button>
                    </ButtonGroup>
                </FooterActions>
            </Footer>
        </Container>
    );
};

export const UploadDataPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const { applicationState } = useApplicationContext();
    const { id: jobArchitectureId } = useParams<{ id: string }>();

    if (is.undefined(jobArchitectureId)) {
        return null;
    }

    const isDashboardV2Enabled = applicationState.featureFlagService.isFeatureFlagEnabled('dashboard-v2');

    return (
        <UploadDataContent
            jobArchitectureId={jobArchitectureId}
            onBack={() => navigate('../company-information')}
            onComplete={() =>
                navigate(
                    isDashboardV2Enabled
                        ? `/job-architecture-v2/${jobArchitectureId}/generating`
                        : `/job-architecture-v2/${jobArchitectureId}/overview?tab=board`,
                )
            }
        />
    );
};
